import { InputJob, isInputJobUnblocked, Job } from "~/components/build/Show/lib/types";
import { BlockStep } from "~/lib/pipeline/Step/BlockStep";
import { CommandStep } from "~/lib/pipeline/Step/CommandStep";
import { GroupStep } from "~/lib/pipeline/Step/GroupStep";
import { InputStep } from "~/lib/pipeline/Step/InputStep";
import { TriggerStep } from "~/lib/pipeline/Step/TriggerStep";
import { WaitStep } from "~/lib/pipeline/Step/WaitStep";
import { isInputJob } from "~/components/build/Show/lib/types/InputJob";

export type Step = CommandStep | GroupStep | InputStep | WaitStep | BlockStep | TriggerStep;

export enum State {
  Ignored = "ignored",
  Ready = "ready",
  Running = "running",
  Failing = "failing",
  Finished = "finished",
  WaitingForDependencies = "waiting_for_dependencies",
}

export enum Outcome {
  Neutral = "neutral",
  Passed = "passed",
  SoftFailed = "soft_failed",
  HardFailed = "hard_failed",
  Errored = "errored",
}

export enum Type {
  Command = "command",
  Group = "group",
  Input = "input",
  Wait = "wait",
  Block = "block",
  Trigger = "trigger",
}

export enum DependencyType {
  Gate = "gate",
  Chain = "chain",
  Direct = "direct",
  Upload = "upload",
}

export type BaseStep = {
  uploadedByStepUuid?: string | null;
  uuid: string;
  type: Type;
  key?: string;
  label?: string;
  state?: State | null;
  outcome?: Outcome | null;
  groupUuid?: string;
  dependencies: {
    uuid: string | null;
    type: DependencyType;
    key?: string;
  }[];
  missingDependencies?: string[];
  if?: string;
  statistics?: {
    retriesCount: number;
    jobsFinishedCount: number;
    jobsCount: number;
    latestJobStartedAt: string | null;
    latestJobFinishedAt: string | null;
  };
};

// Helper functions to determine the step state

export function isCanceled(step: Step) {
  return step.state === State.Finished && step.outcome === Outcome.Errored;
}

export function isFailed(step: Step) {
  return (
    step.state === State.Failing ||
    (step.state === State.Finished && step.outcome === Outcome.HardFailed)
  );
}

export function isPassed(step: Step) {
  return (
    (step.state === State.Finished && step.outcome === Outcome.Passed) ||
    (step.type === Type.Input && step.state === State.Finished && step.outcome === Outcome.Neutral)
  );
}

export function isRunning(step: Step) {
  return step.state === State.Running;
}

export function isScheduled(step: Step) {
  return step.state === State.Ready;
}

export function isSoftFailed(step: Step) {
  return step.state === State.Finished && step.outcome === Outcome.SoftFailed;
}

export function isWaiting(step: Step) {
  return step.state === State.WaitingForDependencies;
}

/**
 * "Awaiting Input" is currently derived from the jobs associated with the step.
 */
export function isAwaitingInput(step: Step, stepJobs: Map<string, Job[]>): boolean {
  if (step.type === Type.Input || step.type === Type.Block) {
    const jobs = stepJobs.get(step.uuid);
    return Boolean(jobs?.some((job) => !isInputJobUnblocked(job as InputJob)));
  }

  if (step.type === Type.Group) {
    return Array.from(stepJobs.values()).some((jobs) => {
      return jobs.some(
        (job) => isInputJob(job) && job.groupUuid === step.uuid && !isInputJobUnblocked(job),
      );
    });
  }

  return false;
}

export function labelForStep(step: Step): string {
  switch (step.type) {
    case "command":
      return step.label || step.commandScript || "";
    case "input":
    case "block":
      return step.label || "Continue";
    default:
      return step.label || "";
  }
}
